import { useRef, useEffect } from 'react';
// @ts-ignore
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
// @ts-ignore
import { useContentToken, EditableToken, RichTextContent, useSlotGroup, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

const MOSAIC_DEFAULTS = [
  'https://multihubmedia.com/cdn-cgi/image/format=auto,w=1280/company/5767906449162419/019cfd5d-84ec-72c2-b4bd-0834d6b79535.webp',
  'https://multihubmedia.com/cdn-cgi/image/format=auto,w=1280/company/5767906449162419/019cfd5d-01bd-7581-ba69-bec151388120.webp',
  'https://multihubmedia.com/cdn-cgi/image/format=auto,w=1280/company/5767906449162419/019cfd5d-0c8b-7108-860c-544bb2f989d1.webp',
  'https://multihubmedia.com/cdn-cgi/image/format=auto,w=1280/company/5767906449162419/019cfd5d-16b6-7312-aa69-20be15324d9c.webp',
];

function mosaicSlotDef(index: number): SlotGroupDefinition {
  return {
    label: `Mosaic Photo ${index + 1}`,
    fields: {
      image: { label: 'Image', type: 'image', defaultValue: MOSAIC_DEFAULTS[index] },
      alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
    },
  };
}

const MOSAIC_SLOT_DEFS: Record<string, SlotGroupDefinition> = {
  'richtext.mosaic_0': mosaicSlotDef(0),
  'richtext.mosaic_1': mosaicSlotDef(1),
  'richtext.mosaic_2': mosaicSlotDef(2),
  'richtext.mosaic_3': mosaicSlotDef(3),
};

export function AboutSection() {
  const label = useContentToken('richtext.label');
  const title = useContentToken('richtext.title');
  const ctaText = useContentToken('richtext.cta_text');
  const ctaUrl = useContentToken('richtext.cta_url');

  const mosaic0 = useSlotGroup('richtext.mosaic_0', MOSAIC_SLOT_DEFS['richtext.mosaic_0']);
  const mosaic1 = useSlotGroup('richtext.mosaic_1', MOSAIC_SLOT_DEFS['richtext.mosaic_1']);
  const mosaic2 = useSlotGroup('richtext.mosaic_2', MOSAIC_SLOT_DEFS['richtext.mosaic_2']);
  const mosaic3 = useSlotGroup('richtext.mosaic_3', MOSAIC_SLOT_DEFS['richtext.mosaic_3']);

  const decorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrapper = decorRef.current;
    if (!wrapper) return;
    const svg = wrapper.querySelector('svg');
    if (!svg) return;

    type Vec3 = [number, number, number];
    const V: Vec3[] = [
      [-1,-1,-1],[1,-1,-1],[1,1,-1],[-1,1,-1],
      [-1,-1,1],[1,-1,1],[1,1,1],[-1,1,1],
    ];
    const E: [number,number][] = [
      [0,1],[1,2],[2,3],[3,0],
      [4,5],[5,6],[6,7],[7,4],
      [0,4],[1,5],[2,6],[3,7],
    ];
    const FACES: number[][] = [
      [0,1,2,3],[7,6,5,4],[0,3,7,4],[1,5,6,2],[3,2,6,7],[0,4,5,1],
    ];
    const EDGE_FACES: [number,number][] = [
      [0,5],[0,3],[0,4],[0,2],[1,5],[1,3],[1,4],[1,2],[2,5],[3,5],[3,4],[2,4],
    ];

    // Calibrated to match the Case & Associates logo cube
    const INIT_RY = Math.PI / 4;   // 45° — left-right symmetric
    const INIT_RX = -1.1261;       // -64.5° — steep top-down view matching logo
    const SCALE = 50.2, CX = 75, CY = 85;

    function applyRotY(v: Vec3, a: number): Vec3 {
      const c = Math.cos(a), s = Math.sin(a);
      return [v[0]*c + v[2]*s, v[1], -v[0]*s + v[2]*c];
    }
    function applyRotX(v: Vec3, a: number): Vec3 {
      const c = Math.cos(a), s = Math.sin(a);
      return [v[0], v[1]*c - v[2]*s, v[1]*s + v[2]*c];
    }
    function project(v: Vec3): [number, number] {
      return [CX + v[0] * SCALE, CY - v[1] * SCALE];
    }

    const NS = 'http://www.w3.org/2000/svg';
    const lines = E.map(() => {
      const ln = document.createElementNS(NS, 'line');
      ln.setAttribute('stroke', 'currentColor');
      ln.setAttribute('stroke-width', '2.4');
      ln.setAttribute('stroke-linecap', 'round');
      svg.appendChild(ln);
      return ln;
    });

    // Only 4 edges are trimmed (matching the logo's open-frame look).
    // Value = 0 means trim the start (first vertex), 1 means trim the end (second vertex).
    const TRIM_EDGES: Record<number, number> = {
      0: 1,   // [0,1] BL→B:      trim at B end
      2: 1,   // [2,3] C_vis→L:   trim at L end (far end)
      5: 1,   // [5,6] BR→R:      trim at R end
      10: 0,  // [2,6] C_vis→R:   trim at C_vis end (near end)
    };
    const TRIM = 0.18;

    function render() {
      const rect = wrapper.getBoundingClientRect();
      const offset = rect.top + rect.height / 2 - window.innerHeight / 2;
      const ry = INIT_RY - offset * 0.000345;
      const rx = INIT_RX;
      const pts = V.map(v => project(applyRotX(applyRotY(v, ry), rx)));

      const faceVis = FACES.map(face => {
        const [a, b, c] = [pts[face[0]], pts[face[1]], pts[face[2]]];
        return (b[0]-a[0])*(c[1]-a[1]) - (b[1]-a[1])*(c[0]-a[0]) < 0;
      });

      // Blend alternating edges toward red based on scroll distance
      const redAmount = Math.min(1, Math.abs(offset) / 800);

      E.forEach(([a, b], i) => {
        const visible = faceVis[EDGE_FACES[i][0]] || faceVis[EDGE_FACES[i][1]];
        let x1 = pts[a][0], y1 = pts[a][1];
        let x2 = pts[b][0], y2 = pts[b][1];

        if (i in TRIM_EDGES) {
          if (TRIM_EDGES[i] === 0) {
            x1 += (x2 - x1) * TRIM;
            y1 += (y2 - y1) * TRIM;
          } else {
            x2 += (x1 - x2) * TRIM;
            y2 += (y1 - y2) * TRIM;
          }
        }

        if (i % 2 === 0) {
          const r = Math.round(204 + (237 - 204) * redAmount);
          const g = Math.round(204 - 204 * redAmount);
          const b2 = Math.round(204 - (204 - 36) * redAmount);
          lines[i].setAttribute('stroke', `rgb(${r},${g},${b2})`);
        } else {
          lines[i].setAttribute('stroke', 'currentColor');
        }

        lines[i].setAttribute('x1', x1.toFixed(2));
        lines[i].setAttribute('y1', y1.toFixed(2));
        lines[i].setAttribute('x2', x2.toFixed(2));
        lines[i].setAttribute('y2', y2.toFixed(2));
        lines[i].style.opacity = visible ? '1' : '0';
      });
    }

    render();
    const onScroll = () => render();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      lines.forEach(ln => ln.parentNode?.removeChild(ln));
    };
  }, []);

  return (
    <section className="case-about">
      <div className="case-container">
        <div className="case-about-grid">
          <div>
            <div style={{ display: 'block' }}>
              <EditableToken path="richtext.label" type="text" label="Section Label" defaultValue={contentSchema.tokens['richtext.label'].defaultValue} as="p" style={{ textTransform: 'uppercase', fontWeight: 600, marginBottom: 25 }}>
                {label}
              </EditableToken>
            </div>
            <div style={{ display: 'block' }}>
              <EditableToken path="richtext.title" type="text" label="Title" defaultValue={contentSchema.tokens['richtext.title'].defaultValue} as="h2" style={{ fontSize: '2rem', fontWeight: 700, marginBottom: 25 }}>
                {title}
              </EditableToken>
            </div>
            <RichTextContent
              path="richtext.body"
              label="Body Text"
              defaultValue={contentSchema.tokens['richtext.body'].defaultValue}
              as="p"
            />
            <EditableToken path="richtext.cta_url" type="url" label="CTA URL" defaultValue={contentSchema.tokens['richtext.cta_url'].defaultValue} as="span" style={{ display: 'contents' }}>
              <EditableToken path="richtext.cta_text" type="text" label="CTA Button Text" defaultValue={contentSchema.tokens['richtext.cta_text'].defaultValue} as="span" style={{ display: 'contents' }}>
                <a className="case-btn" href={ctaUrl}>{ctaText}</a>
              </EditableToken>
            </EditableToken>
          </div>

          <div className="case-mosaic">
            <EditableSlotGroup groupPath="richtext.mosaic_0" definition={MOSAIC_SLOT_DEFS['richtext.mosaic_0']} className="case-mosaic-first">
              {mosaic0.image && <VibesitesImage className="case-mosaic-img" url={mosaic0.image} alt={mosaic0.alt || ''} sizes="(min-width: 768px) 28vw, 50vw" />}
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="richtext.mosaic_1" definition={MOSAIC_SLOT_DEFS['richtext.mosaic_1']} className="case-mosaic-second">
              {mosaic1.image && <VibesitesImage className="case-mosaic-img" url={mosaic1.image} alt={mosaic1.alt || ''} sizes="(min-width: 768px) 17vw, 32vw" />}
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="richtext.mosaic_2" definition={MOSAIC_SLOT_DEFS['richtext.mosaic_2']} className="case-mosaic-third">
              {mosaic2.image && <VibesitesImage className="case-mosaic-img" url={mosaic2.image} alt={mosaic2.alt || ''} sizes="(min-width: 768px) 16vw, 30vw" />}
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="richtext.mosaic_3" definition={MOSAIC_SLOT_DEFS['richtext.mosaic_3']} className="case-mosaic-fourth">
              {mosaic3.image && <VibesitesImage className="case-mosaic-img" url={mosaic3.image} alt={mosaic3.alt || ''} sizes="(min-width: 768px) 18vw, 34vw" />}
            </EditableSlotGroup>
            <div className="case-mosaic-decor" ref={decorRef}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="-1 -6 152 181" width="120" height="143" style={{ overflow: 'visible' }} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'RichText',
  displayName: 'About Section',
  tokens: {
    'richtext.label': {
      label: 'Section Label',
      type: 'text',
      defaultValue: 'ABOUT US',
    },
    'richtext.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'Property management with heart',
    },
    'richtext.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultValue: 'Care for our residents. Care for our employees. That\u2019s the mantra at the center of Case & Associates.',
    },
    'richtext.cta_text': {
      label: 'CTA Button Text',
      type: 'text',
      defaultValue: 'About Us',
    },
    'richtext.cta_url': {
      label: 'CTA URL',
      type: 'url',
      defaultValue: '/about-us/',
    },
  },
  slotGroups: {
    'richtext.mosaic_0': {
      label: 'Mosaic Photo 1',
      fields: {
        image: { label: 'Image', type: 'image', defaultValue: 'https://multihubmedia.com/cdn-cgi/image/format=auto,w=1280/company/5767906449162419/019cfd5d-84ec-72c2-b4bd-0834d6b79535.webp' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
      },
    },
    'richtext.mosaic_1': {
      label: 'Mosaic Photo 2',
      fields: {
        image: { label: 'Image', type: 'image', defaultValue: 'https://multihubmedia.com/cdn-cgi/image/format=auto,w=1280/company/5767906449162419/019cfd5d-01bd-7581-ba69-bec151388120.webp' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
      },
    },
    'richtext.mosaic_2': {
      label: 'Mosaic Photo 3',
      fields: {
        image: { label: 'Image', type: 'image', defaultValue: 'https://multihubmedia.com/cdn-cgi/image/format=auto,w=1280/company/5767906449162419/019cfd5d-0c8b-7108-860c-544bb2f989d1.webp' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
      },
    },
    'richtext.mosaic_3': {
      label: 'Mosaic Photo 4',
      fields: {
        image: { label: 'Image', type: 'image', defaultValue: 'https://multihubmedia.com/cdn-cgi/image/format=auto,w=1280/company/5767906449162419/019cfd5d-16b6-7312-aa69-20be15324d9c.webp' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
      },
    },
  },
};

(AboutSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AboutSection;
